import React, { useEffect, useState, useRef } from "react";
import { Dialog, Button } from "@material-ui/core";
import useTranslation from "Hooks/useTranslation";
import { apiBaseUrl } from "config";
import axios from "axios";
import 'maplibre-gl/dist/maplibre-gl.css';
import "maplibre-gl-js-amplify/dist/public/amplify-map.css";
import maplibregl from 'maplibre-gl';
import { Cached } from "@material-ui/icons";


const MapComponentModal = (props) => {
  const { isOpen, onCancel, gpsData, currentUnitModel, section, currentJob } = props;

  const translation = useTranslation();
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [displayGpsData, setDisplayGpsData] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const timestampMarkerRef = useRef(null);

  useEffect(() => {
    if (isOpen && gpsData) {
      setLat(gpsData.latitude);
      setLng(gpsData.longitude);
      setDisplayGpsData(gpsData);
    }
  }, [gpsData?.latitude, gpsData?.longitude, gpsData?.timestamp, isOpen, gpsData]);

const formatTimestampInUserTimezone = (timestampMs) => {
  if (timestampMs == null || Number.isNaN(Number(timestampMs))) return null;
  return new Date(Number(timestampMs)).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "medium",
  });
};

const refreshMap = async () => {
  const urlMap =
    section === "jobs"
      ? `${apiBaseUrl}/data/${section}/gps/${currentJob?.id}`
      : `${apiBaseUrl}/data/${section}/gps/${currentUnitModel?.uId}`;

  try {
    const { data } = await axios.get(urlMap);
    setLat(data.latitude);
    setLng(data.longitude);
    setDisplayGpsData(data);
    return data;
  } catch (error) {
    console.error("Error fetching map coordinates:", error);
    throw error;
  }
};

  const handleClose = () => {
    setLat(null);
    setLng(null);
    setDisplayGpsData(null);
    if (timestampMarkerRef.current) {
      timestampMarkerRef.current.remove();
      timestampMarkerRef.current = null;
    }
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    onCancel();
  };

  useEffect(() => {
    if (lat !== null && lng !== null && isOpen) {
      initializeMap();
      return () => {
        if (timestampMarkerRef.current) {
          timestampMarkerRef.current.remove();
          timestampMarkerRef.current = null;
        }
        if (markerRef.current) {
          markerRef.current.remove();
          markerRef.current = null;
        }
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initializeMap uses lat, lng, displayGpsData from closure
  }, [lat, lng, isOpen, displayGpsData?.timestamp]);

  function initializeMap() {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const apiKey = "v1.public.eyJqdGkiOiI5MzcwNDFkYi02M2UzLTQ1MzgtODNiYi0xOGQwN2ZkMDUzNDcifYfo7-yc6R6yYy_LemT0RbeOdUSpRBH4lCtahgwsWgQ6WPZtZ8l4PgQgcIzZdQ7soyv2Opt3vp9a1Rf5ULwLKg71PcHmUB6ni2zPrtv_v8yOsPMjx5EVng432nPonmOIsKVw-A-7D16ajmG1hgSfulSUtEYDtujUSR0Bdrih2goGUhtLkd7rWrQS3KM71qVvJ6oDTYBQdmmTP6sBTef_lJaM7BCTvu0APvjZJxiZy9ZgTaIPYFp3esRtOQ97Zl5mnjhLfpTwMqE_VK3zryHoGDO6MT_P53recN9iWGwc88ZU7QVsE2NlaNnK2ImjTSlWXN677RPCnWvxT24SHQgn3x0.Y2MzMTcwNWYtZTgwMy00NGZlLWFiNGItNzVkMDFmMDM0Nzcy";
    const mapName = "dms-map";
    const region = "eu-central-1";

    const map = new maplibregl.Map({
      container: 'map',
      style: `https://maps.geo.${region}.amazonaws.com/maps/v0/maps/${mapName}/style-descriptor?key=${apiKey}`,
      center: [lng, lat],
      zoom: 11
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-left");

    const timestampLabel = formatTimestampInUserTimezone(displayGpsData?.timestamp) || "—";
    const marker = new maplibregl.Marker({ color: "#d32f2f" })
      .setLngLat([lng, lat])
      .addTo(map);
    markerRef.current = marker;

    const timestampEl = document.createElement("div");
    timestampEl.style.cssText = `
      font-size: 11px;
      line-height: 1.2;
      color: #333;
      white-space: nowrap;
      text-shadow: 0 0 2px #fff, 0 1px 2px #fff, 1px 0 2px #fff, -1px 0 2px #fff, 0 -1px 2px #fff;
      background: white;
      padding: 2px 5px;
      border: 1px solid #ccc;
      border-radius: 3px;
    `;
    timestampEl.textContent = timestampLabel;

    const timestampMarker = new maplibregl.Marker({
      element: timestampEl,
      anchor: "top",
      offset: [0, 12],
    })
      .setLngLat([lng, lat])
      .addTo(map);
    timestampMarkerRef.current = timestampMarker;
  }
  
  return (
    <Dialog open={isOpen} className="mapModal" onClose={handleClose}>
      <div className="modalTitleHolder mb16">
        <h1 className="modalTitle text-center">UNIT CURRENT LOCATION</h1>
      </div>
  
      <div id="map" style={{ height: "500px" }} />
  
      <div className="btnHolder end mt16 fixedWidthBtnWithoutMargin">
        <Button className="refreshButton" startIcon={<Cached />} onClick={refreshMap}>
          <span className="refreshButtonLabel">{translation.refreshButtonLabel}</span>
        </Button>
        <Button onClick={handleClose} variant="outlined" color="primary">
          {translation.closeButtonLabel}
        </Button>
      </div>
    </Dialog>
  );
}  

export default MapComponentModal;
